import React, { useRef } from 'react';
import { MainProduct } from '../../types/studio';
import { useStudioStore } from '../../stores/useStudioStore';

interface Props {
  product: MainProduct;
  index: number;
}

export const ProductCard: React.FC<Props> = ({ product, index }) => {
  const { updateMainProduct, removeMainProduct } = useStudioStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelInputRef = useRef<HTMLInputElement>(null);

  const handleProductFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const newImages = Array.from(e.target.files).map(file => ({
      file,
      previewUrl: URL.createObjectURL(file)
    }));
    updateMainProduct(product.id, { images: [...product.images, ...newImages] });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleModelFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    updateMainProduct(product.id, { 
      modelImage: { file, previewUrl: URL.createObjectURL(file) } 
    });
    if (modelInputRef.current) modelInputRef.current.value = '';
  };

  const removeProductImage = (i: number) => {
    const newImages = [...product.images];
    URL.revokeObjectURL(newImages[i].previewUrl);
    newImages.splice(i, 1);
    updateMainProduct(product.id, { images: newImages });
  };

  const removeModelImage = () => {
    if (product.modelImage) URL.revokeObjectURL(product.modelImage.previewUrl);
    updateMainProduct(product.id, { modelImage: undefined });
  };

  return (
    <div className="product-card">
      <div className="product-card-header">
        <div className="product-card-title">Product {index}</div>
        <button className="product-card-remove" onClick={() => removeMainProduct(product.id)}>×</button>
      </div>
      
      <div className="product-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 14px' }}>
        {/* Drop zones side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {/* Drop zone for Product images */}
          <div className="card-drop-zone">
            {product.images.length === 0 ? (
              <>
                <div className="card-drop-zone-icon">+</div>
                <div className="card-drop-zone-label">Product images</div>
                <div className="card-drop-zone-hint">(Drag and drop)</div>
              </>
            ) : (
              <div className="card-thumbs">
                {product.images.map((img, i) => (
                  <div key={i} className="card-thumb-wrap">
                    <img src={img.previewUrl} alt={`Ref ${i}`} />
                    <button className="card-thumb-rm" onClick={(e) => { e.preventDefault(); removeProductImage(i); }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <input 
              type="file" 
              multiple 
              accept="image/png, image/jpeg, image/webp" 
              ref={fileInputRef}
              onChange={handleProductFile} 
              disabled={product.images.length >= 4}
            />
          </div>

          {/* Drop zone for Model image */}
          <div className="card-drop-zone">
            {!product.modelImage ? (
              <>
                <div className="card-drop-zone-icon">+</div>
                <div className="card-drop-zone-label">Model image</div>
                <div className="card-drop-zone-hint">(Drag and drop)</div>
              </>
            ) : (
              <div className="card-thumbs">
                <div className="card-thumb-wrap">
                  <img src={product.modelImage.previewUrl} alt="Model" />
                  <button className="card-thumb-rm" onClick={(e) => { e.preventDefault(); removeModelImage(); }}>×</button>
                </div>
              </div>
            )}
            <input 
              type="file" 
              accept="image/png, image/jpeg, image/webp" 
              ref={modelInputRef}
              onChange={handleModelFile} 
            />
          </div>
        </div>

        {/* Product ID input below drop zones */}
        <div className="pid-wrap">
          <input 
            type="text" 
            className="pid-input" 
            placeholder="Product ID (e.g. 4088)" 
            value={product.productId}
            onChange={e => updateMainProduct(product.id, { productId: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};
